import {callAPI} from '@/store/utils';
import {fixtures, useFixtures} from '@/store/assistedCategorizationFixtures';

const FYGR_COMPANY = 'FYGR_COMPANY';

// Clé de rejet du parcours, par company. On n'utilise PAS le chapitre
// d'onboarding `categorize_transactions` : il pilote déjà le tuto
// `categorization-overview` (cf. src/tours/catalog.ts), le compléter ici
// masquerait le tuto par effet de bord. Si le back ajoute un chapitre dédié
// (`assisted_categorization`), il suffira de remplacer ces deux helpers.
const DISMISSED_KEY = 'FYGR_ASSISTED_CATEG_DISMISSED';

const dismissedKeyFor = (companyId) => `${DISMISSED_KEY}:${companyId}`;

// Les deux passes LLM (C2, C6) et la préparation du plan (C8) sont longues :
// le timeout par défaut de callAPI (20 s) est trop court. 120 s est la limite
// au-delà de laquelle l'écran 2a bascule en erreur.
const LLM_TIMEOUT = 120;
// callAPI relance automatiquement une fois en cas de timeout (`recall < 1`).
// Sur ces appels on ne veut pas : une seconde passe LLM silencieuse coûte cher
// et l'utilisateur a un bouton « Réessayer » explicite.
const NO_AUTO_RETRY = 1;

// Une suggestion porte déjà ses mots-clés sous forme de tableau — ils se
// combinent en ET (« john » ET « doe »). On les cale sur quatre champs, la
// limite du moteur de règles.
const KEYWORD_FIELDS = 4;

export const padKeywords = (keywords) => {
  const parts = (Array.isArray(keywords) ? keywords : [keywords])
    .map((keyword) => String(keyword ?? '').trim())
    .filter(Boolean)
    .slice(0, KEYWORD_FIELDS);
  return Array.from({length: KEYWORD_FIELDS}, (_, i) => parts[i] || '');
};

// Suggestions sans catégorie : regroupées à part plutôt que masquées — les
// cacher sous-estimerait le plan.
export const UNCATEGORISED = '__uncategorised__';

const categoryKeyOf = (suggestion) =>
  suggestion.category ??
  suggestion.categoryId ??
  suggestion.category_id ??
  UNCATEGORISED;

// Une suggestion porte l'ID de sa catégorie, pas son nom : le libellé et le
// chemin complet se résolvent contre l'arbre (`app.categories`), dont les
// enfants vivent sous `list`.
const categoryTrail = (categories, categoryId, trail = []) => {
  for (const category of categories || []) {
    const next = [...trail, category.name];
    if (category.id === categoryId) return next;
    const found = categoryTrail(category.list, categoryId, next);
    if (found) return found;
  }
  return null;
};

export const resolveCategory = (categories, categoryId) => {
  const trail = categoryTrail(categories, categoryId);
  if (!trail || !trail.length) return {leaf: '', path: ''};
  return {leaf: trail[trail.length - 1], path: trail.join(' › ')};
};

// Les questions arrivent sous deux formes selon l'endpoint (`start` renvoie
// `text`/`options`, l'état renvoie `question_text`/`question_options`). On
// normalise une bonne fois pour que les écrans n'aient qu'un seul contrat.
export const normaliseQuestion = (question) => ({
  id: question.id,
  key: question.question_key ?? null,
  position: question.position ?? 0,
  text: question.question_text ?? question.text ?? '',
  why_it_matters: question.why_it_matters ?? '',
  // Suggestions couvertes par la question : sert au volume affiché et à
  // l'aperçu « voir les opérations ».
  suggestionIds: question.suggestion_ids ?? [],
  // `allows_free_text` décide si le champ libre est proposé.
  allowsFreeText: question.allows_free_text ?? true,
  options: (question.question_options ?? question.options ?? []).map(
    (option) => ({
      // L'état renvoie `id: null` sur les options de catégorie : on retombe
      // alors sur `category_id`, seul identifiant disponible.
      id: option.id ?? option.category_id ?? null,
      label: option.label,
      categoryId: option.category_id ?? null,
    }),
  ),
});

export const normaliseQuestions = (questions) =>
  (questions || [])
    .map(normaliseQuestion)
    .sort((a, b) => a.position - b.position);

// Réponses déjà enregistrées côté serveur : on les restaure pour que l'écran 3
// reprenne là où l'utilisateur s'était arrêté.
const answersFromQuestions = (questions) =>
  (questions || []).reduce((acc, question) => {
    if (!question.is_answered && !question.answer_free_text) return acc;
    acc[question.id] = {
      optionId: question.selected_option_id ?? null,
      dontKnow: question.is_answered && !question.selected_option_id,
      freeText: question.answer_free_text || '',
    };
    return acc;
  }, {});

// `tree_actions` → propositions de l'écran 5. Le type du back porte le verbe ;
// le libellé et le chemin se résolvent ensuite contre l'arbre (getter
// `categoryProposalDisplay`), car l'action ne porte que des identifiants.
const ACTION_VERBS = {
  create_category: 'create',
  rename_category: 'rename',
  delete_category: 'delete',
};

const normaliseTreeActions = (treeActions) =>
  (treeActions || []).map((action) => ({
    id: action.action_id ?? action.id,
    action: ACTION_VERBS[action.type] ?? action.type,
    name: action.name,
    parentId: action.parent_id ?? null,
    categoryId: action.category_id ?? null,
    reason: action.reason ?? '',
  }));

const emptyDraft = () => ({
  // Ignorer les opérations catégorisées à la main (drapeau global de C11).
  skipManual: false,
  // Par id de proposition d'arborescence : {checked}. Refuser une proposition
  // bloque les règles qui en dépendent, donc les exclut de la conversion ; en
  // revanche l'action elle-même (créer/renommer la catégorie) n'est transmise
  // par aucun endpoint connu à ce jour.
  categories: {},
  // Par catégorie cible : {checked} — c'est la case de la carte.
  groups: {},
  // Par suggestion : {keywords[4], enabled, invoices, affected} — une
  // suggestion EST une ligne du panneau d'édition.
  suggestions: {},
});

// Construit la copie de travail à partir du plan. `checked` par défaut vient du
// back (`checked`/`default_checked`) : une carte peut arriver décochée.
const draftFromPlan = (plan) => {
  const draft = emptyDraft();
  (plan?.categoryProposals || []).forEach((proposal) => {
    draft.categories[proposal.id] = {
      checked: proposal.checked ?? proposal.default_checked ?? true,
    };
  });
  (plan?.rules || []).forEach((suggestion) => {
    const groupKey = categoryKeyOf(suggestion);
    // La case de la carte : cochée si au moins une de ses suggestions l'est.
    const checked = suggestion.checked ?? suggestion.default_checked ?? true;
    draft.groups[groupKey] = {
      checked: draft.groups[groupKey]?.checked || checked,
    };
    draft.suggestions[suggestion.id] = {
      // Les mots-clés d'une suggestion se combinent en ET : c'est UNE règle,
      // pas une par mot-clé.
      keywords: padKeywords(suggestion.keywords),
      enabled: checked,
      // Décidé ligne par ligne : cette règle vaut-elle aussi pour les
      // échéances et factures ?
      invoices: suggestion.isForInvoice ?? suggestion.is_for_invoice ?? true,
      // Le compte arrive avec la suggestion : plus besoin d'aller le chercher.
      affected: suggestion.affected ?? suggestion.count ?? 0,
    };
  });
  return draft;
};

// State en fabrique (et non littéral partagé) : le parcours a un cycle de vie
// propre et doit repartir vierge à chaque instanciation du store.
const initialState = () => ({
  // 'idle' → 'analyzing' (C2) → 'questions' → 'preparing' (C6) → 'ready' (C8)
  status: 'idle',
  // Identifiant de session renvoyé par C2, propagé à C6 / C8 / C11.
  sessionId: null,
  // Métadonnées de la session : `iteration` (1 = première passe), `status`
  // ('awaiting_input'…), `suggestionCount` (nombre de suggestions trouvées),
  // `truncatedSuggestionCount` (suggestions tronquées faute de place).
  sessionMeta: {
    iteration: 0,
    status: null,
    suggestionCount: 0,
    truncatedSuggestionCount: 0,
  },
  // Chemin emprunté écran 1 : 'imported' | 'copied' | 'scratch' (le back
  // accepte aussi 'kept', non atteignable depuis l'UI actuelle). C'est la
  // seule chose que C2 transporte : elle fixe `has_custom_tree` côté LLM.
  treeSource: null,
  hasCustomTree: false,
  // Nombre d'opérations à analyser, renvoyé par C2 : alimente le compteur
  // de l'écran 2a.
  transactionsToAnalyze: 0,
  // Jalon d'analyse réellement atteint côté back ('reading' | 'grouping' |
  // 'matching' | 'suggesting'). Tant que le back n'en émet pas, les écrans
  // d'attente dérivent l'étape courante du pourcentage parcouru.
  milestone: null,
  // Échantillon C3, partagé par les écrans 2a et 2b : les libellés qui
  // défilent sont les mêmes, inutile de refaire l'appel entre les deux.
  sampleLabels: [],
  // Nombre d'opérations non catégorisées (`recordsFiltered` de C3). Sert de
  // total au compteur tant que C2 n'a pas renvoyé le sien.
  uncategorizedCount: 0,
  // Questions de clarification : elles arrivent AVEC la réponse de C2, il n'y
  // a pas d'appel dédié (C5 = lecture de ce state).
  questions: [],
  // Ligne d'introduction de l'écran 3 : opérations analysées (`reviewed_count`)
  // et nombre de clusters de mots-clés trouvés (les « habitudes récurrentes »).
  analyzedCount: 0,
  clusterCount: 0,
  // Ajustements d'arborescence proposés par la passe 1 : `{action_id, type,
  // name, parent_id, category_id, reason}`. Alimentent l'étape « Vos
  // catégories » de l'écran 5.
  treeActions: [],
  // Verdicts du LLM par suggestion : `{suggestion_id, verdict, keywords,
  // affected, reason}`. `keep` alimente les cartes de l'écran 4, `drop` le
  // bloc « Écarté ». La catégorie cible, elle, vient de `getSuggestionsList`.
  llmSuggestions: [],
  // Suggestions écartées, dérivées des verdicts `drop`.
  droppedSuggestions: [],
  // Le back estime-t-il pouvoir construire le plan ?
  readyToRun: false,
  // Compte dédoublonné par carte, renvoyé par le serveur (`recordsFiltered`
  // sur le lot de suggestions du groupe). Additionner les `affected` compterait
  // plusieurs fois une opération attrapée par deux suggestions du groupe.
  groupCounts: {},
  // Le parcours a-t-il déjà été lancé pour cette company ? `null` = jamais.
  hasSession: false,
  // Réponses de l'écran 3, indexées par id de question :
  // {optionId, kind, categoryId, text}.
  answers: {},
  // Champ libre de bas de page (facultatif).
  generalNote: '',
  // Plan renvoyé par C8. `categoryProposals` est vide en première visite
  // (écran 4) et alimenté aux visites suivantes (écran 5). `dropped` liste les
  // clusters écartés, `coverage` le taux de couverture affiché en en-tête.
  plan: {categoryProposals: [], rules: [], dropped: [], coverage: null},
  // Copie de travail : tout l'écran plan est un seul état vivant. Décocher une
  // ligne de mot-clé doit mettre à jour les chips de la carte, son compteur et
  // le total d'Apply dans le même mouvement — donc une seule source, ici, et
  // pas un état local par composant.
  draft: emptyDraft(),
  error: null,
});

export default {
  namespaced: true,
  getters: {
    // Le CTA de l'écran 3 s'active dès qu'une question a été traitée — y
    // compris en la passant, via « Tout laisser décider » ou en saisissant
    // seulement du texte libre.
    answeredCount: (state) =>
      state.questions.filter((q) => {
        const answer = state.answers[q.id];
        return !!(
          answer?.optionId ||
          answer?.dontKnow ||
          (answer?.freeText || '').trim()
        );
      }).length,

    // Une catégorie proposée est-elle retenue ? Absente du plan (1re visite)
    // → considérée acquise, rien ne dépend d'elle.
    isCategoryKept: (state) => (categoryId) =>
      state.draft.categories[categoryId]?.checked ?? true,

    // Libellé et chemin d'une proposition d'arborescence. « Créer » se situe
    // sous son parent ; « renommer » montre l'ancien nom → le nouveau.
    categoryProposalDisplay: (state, getters, rootState) => (proposal) => {
      const categories = rootState.app?.categories;
      if (proposal.action === 'create') {
        const parent = resolveCategory(categories, proposal.parentId);
        return {
          label: proposal.name,
          path: parent.path ? `${parent.path} › ${proposal.name}` : proposal.name,
        };
      }
      const current = resolveCategory(categories, proposal.categoryId);
      if (proposal.action === 'rename') {
        return {
          label: current.leaf
            ? `${current.leaf} → ${proposal.name}`
            : proposal.name,
          path: current.path,
        };
      }
      return {label: current.leaf || proposal.name, path: current.path};
    },

    // Une règle dont la catégorie requise a été refusée n'a pas lieu d'être :
    // elle est bloquée, grisée, et sort du total d'Apply.
    isRuleBlocked: (state, getters) => (rule) => {
      const requires = rule.requiresCategory ?? rule.requires_category ?? null;
      if (requires == null) return false;
      return !getters.isCategoryKept(requires);
    },

    // Libellé et chemin affichés. `getSuggestionsList` ne renvoie qu'un ID de
    // catégorie : on résout contre l'arbre. Et si la règle dépend d'un
    // renommage refusé, on retombe sur le libellé d'origine. Purement
    // affichage, aucun appel.
    ruleDisplay: (state, getters, rootState) => (rule) => {
      const renamedBy = rule.renamedByCategory ?? rule.renamed_by_category;
      if (renamedBy != null && !getters.isCategoryKept(renamedBy)) {
        return {
          leaf: rule.altLeaf ?? rule.alt_leaf ?? rule.leaf ?? '',
          path: rule.altPath ?? rule.alt_path ?? rule.path ?? '',
        };
      }
      if (rule.leaf || rule.path) {
        return {leaf: rule.leaf ?? '', path: rule.path ?? ''};
      }
      return resolveCategory(
        rootState.app?.categories,
        rule.category ?? rule.categoryId ?? rule.category_id,
      );
    },

    // Les suggestions d'une catégorie. Une carte = une catégorie.
    groupSuggestions: (state) => (categoryId) =>
      (state.plan.rules || []).filter(
        (suggestion) => categoryKeyOf(suggestion) === categoryId,
      ),

    // Chips de la carte : les mots-clés des suggestions actives, à plat et
    // dédoublonnés — plusieurs suggestions d'une même catégorie partagent
    // souvent des termes (« paypal » revient sur quatre d'entre elles).
    groupKeywords: (state, getters) => (categoryId) => {
      const seen = new Set();
      getters.groupSuggestions(categoryId).forEach((suggestion) => {
        if (!state.draft.suggestions[suggestion.id]?.enabled) return;
        state.draft.suggestions[suggestion.id].keywords
          .filter(Boolean)
          .forEach((keyword) => seen.add(keyword));
      });
      return [...seen];
    },

    // Compteur de la carte : somme des suggestions actives. Arithmétique pure
    // sur des comptes déjà connus, aucun appel.
    groupAffected: (state, getters) => (categoryId) =>
      getters
        .groupSuggestions(categoryId)
        .filter((suggestion) => state.draft.suggestions[suggestion.id]?.enabled)
        .reduce(
          (sum, suggestion) =>
            sum + (state.draft.suggestions[suggestion.id]?.affected ?? 0),
          0,
        ),

    // Les cartes, une par catégorie, la plus volumineuse en tête.
    planGroups: (state, getters) => {
      const order = [];
      (state.plan.rules || []).forEach((suggestion) => {
        const key = categoryKeyOf(suggestion);
        if (!order.includes(key)) order.push(key);
      });
      return order
        .map((categoryId) => {
          const suggestions = getters.groupSuggestions(categoryId);
          return {
            categoryId,
            suggestions,
            keywords: getters.groupKeywords(categoryId),
            // Compte serveur si connu, somme locale en attendant.
            affected:
              state.groupCounts[categoryId] ?? getters.groupAffected(categoryId),
            checked: !!state.draft.groups[categoryId]?.checked,
            blocked: suggestions.every((s) => getters.isRuleBlocked(s)),
          };
        })
        .sort((a, b) => b.affected - a.affected);
    },

    // Ce qui partira à l'application : une suggestion retenue est active, dans
    // une carte cochée, et non bloquée.
    keptSuggestions: (state, getters) =>
      (state.plan.rules || []).filter(
        (suggestion) =>
          state.draft.groups[categoryKeyOf(suggestion)]?.checked &&
          state.draft.suggestions[suggestion.id]?.enabled &&
          !getters.isRuleBlocked(suggestion),
      ),

    // Suggestions retouchées dans le panneau : leurs mots-clés ou leur
    // application aux factures diffèrent de ce que le serveur détient. Ce sont
    // celles qu'il faut enregistrer avant la conversion.
    editedSuggestions: (state) =>
      (state.plan.rules || []).filter((suggestion) => {
        const entry = state.draft.suggestions[suggestion.id];
        if (!entry) return false;
        const original = (suggestion.keywords || []).filter(Boolean);
        const current = entry.keywords.filter(Boolean);
        const sameKeywords =
          original.length === current.length &&
          original.every((keyword, i) => keyword === current[i]);
        const originalInvoices =
          suggestion.isForInvoice ?? suggestion.is_for_invoice ?? true;
        return !sameKeywords || entry.invoices !== originalInvoices;
      }),

    // Ce qu'affiche le CTA : le nombre de suggestions qui seront converties.
    // Un compte d'opérations demanderait un dédoublonnage côté serveur — ici
    // il n'y a rien à interpréter, c'est exactement ce qui part dans
    // `convertToRules`.
    applyCount: (state, getters) => getters.keptSuggestions.length,
  },
  state: initialState,
  mutations: {
    setStatus(state, status) {
      state.status = status;
    },
    setMilestone(state, milestone) {
      state.milestone = milestone ?? null;
    },
    setSample(state, {labels, uncategorizedCount}) {
      state.sampleLabels = Array.isArray(labels) ? labels : [];
      state.uncategorizedCount = uncategorizedCount ?? 0;
    },
    setTreeSource(state, {treeSource, hasCustomTree}) {
      state.treeSource = treeSource ?? null;
      state.hasCustomTree = !!hasCustomTree;
    },
    setSession(
      state,
      {
        sessionId,
        questions,
        transactionsToAnalyze,
        analyzedCount,
        clusterCount,
        treeActions,
        droppedSuggestions,
        session,
      },
    ) {
      state.treeActions = Array.isArray(treeActions) ? treeActions : [];
      state.droppedSuggestions = Array.isArray(droppedSuggestions)
        ? droppedSuggestions
        : [];
      if (session) {
        state.sessionMeta = {
          iteration: session.iteration ?? 0,
          status: session.status ?? null,
          suggestionCount: session.suggestion_count ?? 0,
          truncatedSuggestionCount: session.truncated_suggestion_count ?? 0,
        };
      }
      // Sur un réessai le back peut ne pas re-renvoyer l'identifiant : on
      // conserve celui qu'on a plutôt que de le perdre (sinon le réessai
      // suivant ouvrirait un nouveau run).
      // L'identifiant peut venir explicitement ou de l'objet session : les deux
      // formes existent selon l'endpoint, aucune raison d'en privilégier une.
      state.sessionId = sessionId ?? session?.id ?? state.sessionId ?? null;
      state.questions = normaliseQuestions(questions);
      state.transactionsToAnalyze = transactionsToAnalyze ?? 0;
      state.analyzedCount = analyzedCount ?? transactionsToAnalyze ?? 0;
      state.clusterCount = clusterCount ?? 0;
    },
    // C1 — un run laissé en cours est signalé au chargement de /transactions :
    // on le reprend au lieu d'en démarrer un nouveau.
    setResumeSession(state, sessionId) {
      state.sessionId = sessionId ?? null;
    },
    // C1 — état complet du parcours pour cette company. `result` à null veut
    // dire « jamais lancé » : on repart de l'écran 1.
    setRemoteState(state, result) {
      if (!result) {
        // Jamais lancé pour cette company : on repart de zéro, sans traîner
        // une session d'un parcours précédent qui ferait reprendre à la
        // mauvaise étape.
        state.hasSession = false;
        state.sessionId = null;
        state.sessionMeta = {
          iteration: 0,
          status: null,
          suggestionCount: 0,
          truncatedSuggestionCount: 0,
        };
        state.questions = [];
        state.answers = {};
        state.llmSuggestions = [];
        state.droppedSuggestions = [];
        state.treeActions = [];
        state.readyToRun = false;
        return;
      }
      const session = result.session || null;
      state.hasSession = !!session;
      if (session) {
        state.sessionId = session.id ?? null;
        state.sessionMeta = {
          iteration: session.iteration ?? 0,
          status: session.status ?? null,
          suggestionCount: session.suggestion_count ?? 0,
          truncatedSuggestionCount: session.truncated_suggestion_count ?? 0,
        };
        state.hasCustomTree = !!session.has_custom_tree;
        state.clusterCount = session.suggestion_count ?? 0;
      }
      const suggestions = result.suggestions || [];
      state.llmSuggestions = suggestions;
      // Le verdict `drop` porte le motif : c'est le bloc « Écarté ».
      state.droppedSuggestions = suggestions
        .filter((s) => s.verdict === 'drop')
        .map((s) => ({
          suggestion_id: s.suggestion_id,
          reason: s.reason || '',
          count: s.affected ?? 0,
          label: (s.keywords || []).join(' '),
        }));
      // L'état nomme les propositions `tree_proposals`, `start` les nomme
      // `tree_actions` : on accepte les deux.
      state.treeActions = result.tree_proposals ?? result.tree_actions ?? [];
      state.questions = normaliseQuestions(result.questions);
      state.answers = answersFromQuestions(result.questions);
      state.readyToRun = !!result.ready_to_run;
    },
    setAnswers(state, answers) {
      state.answers = {...answers};
    },
    // Choisir une option lève `dontKnow` : les deux s'excluent.
    setAnswer(state, {questionId, option}) {
      const previous = state.answers[questionId] || {};
      state.answers = {
        ...state.answers,
        [questionId]: {
          optionId: option?.id ?? null,
          dontKnow: false,
          // Le texte libre accompagne une option choisie, il ne la remplace
          // pas : on le conserve d'un changement d'option à l'autre.
          freeText: previous.freeText || '',
        },
      };
    },
    // « Je ne sais pas » : le back attend `dont_know`, et l'option choisie
    // saute — les deux s'excluent.
    setAnswerDontKnow(state, {questionId}) {
      const previous = state.answers[questionId] || {};
      state.answers = {
        ...state.answers,
        [questionId]: {
          optionId: null,
          dontKnow: true,
          freeText: previous.freeText || '',
        },
      };
    },
    setAnswerText(state, {questionId, text}) {
      const previous = state.answers[questionId] || {
        optionId: null,
        dontKnow: false,
      };
      state.answers = {
        ...state.answers,
        [questionId]: {...previous, freeText: text},
      };
    },
    setGeneralNote(state, note) {
      state.generalNote = note ?? '';
    },
    // « Tout laisser décider » : `dont_know` sur chaque question d'un coup.
    // Purement client, aucun appel.
    deferAll(state) {
      const answers = {...state.answers};
      state.questions.forEach((question) => {
        answers[question.id] = {
          optionId: null,
          dontKnow: true,
          freeText: answers[question.id]?.freeText || '',
        };
      });
      state.answers = answers;
    },
    setPlan(state, plan) {
      state.plan = {
        // Les ajustements d'arborescence viennent de la passe 1 ; C8 ne fait
        // que compléter si jamais il en renvoie aussi.
        categoryProposals:
          plan?.categoryProposals ?? normaliseTreeActions(state.treeActions),
        rules: plan?.rules ?? [],
        dropped: plan?.dropped ?? state.droppedSuggestions ?? [],
        coverage: plan?.coverage ?? null,
      };
      state.draft = draftFromPlan(state.plan);
    },
    // « Recommencer » : on revient aux valeurs par défaut du plan. Purement
    // client, aucun appel.
    resetDraft(state) {
      state.draft = draftFromPlan(state.plan);
    },
    toggleDraftCategory(state, categoryId) {
      const entry = state.draft.categories[categoryId];
      if (entry) entry.checked = !entry.checked;
    },
    setDraftCategoriesChecked(state, checked) {
      Object.values(state.draft.categories).forEach((entry) => {
        entry.checked = checked;
      });
    },
    // Case de la carte : elle entraîne toutes ses suggestions.
    toggleDraftGroup(state, categoryId) {
      const group = state.draft.groups[categoryId];
      if (!group) return;
      group.checked = !group.checked;
    },
    toggleDraftSuggestion(state, {suggestionId}) {
      const entry = state.draft.suggestions[suggestionId];
      if (entry) entry.enabled = !entry.enabled;
    },
    toggleDraftSuggestionInvoices(state, {suggestionId}) {
      const entry = state.draft.suggestions[suggestionId];
      if (entry) entry.invoices = !entry.invoices;
    },
    setDraftSuggestionKeyword(state, {suggestionId, fieldIndex, value}) {
      const entry = state.draft.suggestions[suggestionId];
      if (entry) entry.keywords[fieldIndex] = value;
    },
    setDraftSuggestionAffected(state, {suggestionId, affected}) {
      const entry = state.draft.suggestions[suggestionId];
      if (entry) entry.affected = affected;
    },
    // ATTENTION : `convertToRules` ne transporte pas ce drapeau. La case est
    // encore rendue dans le pied de l'écran 4 mais n'a aucun effet côté
    // serveur — à câbler (paramètre de l'endpoint) ou à retirer de l'UI.
    setGroupCount(state, {categoryId, count}) {
      state.groupCounts = {...state.groupCounts, [categoryId]: count};
    },
    // Les comptes serveur ne valent plus rien dès qu'une sélection change.
    invalidateCounts(state) {
      state.groupCounts = {};
    },
    setSkipManual(state, skipManual) {
      state.draft.skipManual = !!skipManual;
    },
    setError(state, error) {
      state.error = error ?? null;
    },
    reset(state) {
      Object.assign(state, initialState());
    },
  },
  actions: {
    // C3 — échantillon de libellés bruts pour la bande défilante (libellés
    // seuls : ni montant, ni date). Une vingtaine suffit, la liste boucle.
    // Idempotent : les écrans 2a et 2b le demandent tous les deux, un seul
    // appel part.
    async actionLoadSample({state, commit, dispatch}, {limit = 20} = {}) {
      if (state.sampleLabels.length) return state.sampleLabels;
      const res = await dispatch(
        'bank/actionGetTransactions',
        {noCategories: true, limit, offset: 0},
        {root: true},
      );
      const labels = (res?.data || [])
        .map((t) => t.label || t.description || '')
        .filter(Boolean);
      commit('setSample', {
        labels,
        uncategorizedCount: res?.recordsFiltered || labels.length,
      });
      return labels;
    },
    // C1 — état du parcours pour la company. À appeler à chaque arrivée sur le
    // parcours : c'est lui qui dit s'il y a une session en cours, où elle en
    // est, et ce qu'elle contient déjà.
    // `keepWhenEmpty` : ne pas écraser l'état courant si le back répond
    // « aucune session ». Utilisé quand on ne cherche qu'à récupérer un
    // identifiant en cours de parcours — sinon une réponse vide effacerait la
    // session que `start` vient tout juste de créer.
    async actionGetState({commit}, {keepWhenEmpty = false} = {}) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      let res;
      try {
        res = await callAPI(
          'GET',
          `company/${companyId}/assistedCategorization`,
        );
      } catch (rejected) {
        res = rejected || {};
      }
      const result = res?.error && !res?.result ? null : res?.result ?? null;
      if (!result && keepWhenEmpty) return null;
      commit('setRemoteState', result);
      return result;
    },
    // `answers` et `complete` exigent le sessionId. Certaines réponses de
    // `start` ne le portent pas : dans ce cas on le récupère via l'état (C1),
    // seul endpoint qui le garantit sous `result.session.id`.
    async actionEnsureSession({state, dispatch}) {
      if (state.sessionId) return state.sessionId;
      await dispatch('actionGetState', {keepWhenEmpty: true});
      return state.sessionId;
    },
    // C2 — passe LLM n°1. Le front n'appelle jamais Bedrock : tout se passe
    // côté serveur, on ne fait qu'ouvrir la session et récupérer les questions.
    async actionStart({state, commit, dispatch}, payload = {}) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      commit('setStatus', 'analyzing');
      commit('setError', null);
      const body = {
        companyId,
        is_custom_category_tree: state.hasCustomTree,
        // Réessai (ou reprise d'un run laissé en plan) : on renvoie
        // l'identifiant existant pour que le back reprenne le même run au
        // lieu d'en ouvrir un second. Absent du contrat fourni — à confirmer.
        ...(state.sessionId ? {sessionId: state.sessionId} : {}),
        ...payload,
      };
      let res;
      try {
        res = useFixtures()
          ? await fixtures.start()
          : await callAPI(
              'POST',
              'assistedCategorization/start',
              body,
              false,
              LLM_TIMEOUT,
              NO_AUTO_RETRY,
            );
      } catch (rejected) {
        // callAPI rejette sur un statut HTTP non-2xx, mais en propageant le
        // corps : « session déjà ouverte » arrive potentiellement par là.
        res = rejected || {};
      }
      // Une session déjà ouverte n'est pas un échec : le back la renvoie, on la
      // reprend au lieu d'en ouvrir une seconde.
      const session = res?.result?.session ?? null;
      if (res?.error && !session) {
        commit('setStatus', 'error');
        commit('setError', res.message || res.error);
        return res;
      }
      commit('setSession', {
        session,
        sessionId:
          session?.id ??
          res.result?.session_id ??
          res.result?.sessionId ??
          null,
        questions: res.result?.questions,
        transactionsToAnalyze:
          res.result?.transactions_count ?? res.result?.transactionsCount ?? 0,
        // `reviewed_count` = opérations effectivement passées en revue.
        analyzedCount:
          res.result?.reviewed_count ?? res.result?.analyzed_count ?? null,
        // « Habitudes récurrentes » = nombre de suggestions trouvées.
        clusterCount:
          res.result?.cluster_count ??
          session?.suggestion_count ??
          0,
        // Ajustements d'arborescence proposés (écran 5) et suggestions
        // écartées (bloc « Écarté » de l'écran 4) : les deux arrivent avec la
        // passe 1, pas avec le plan.
        treeActions: res.result?.tree_actions,
        droppedSuggestions: res.result?.dropped_suggestions,
      });
      // `start` ne renvoie pas toujours l'identifiant de session : sans lui,
      // `answers`/`complete` échoueraient en `missing_session_id`.
      if (!state.sessionId) {
        await dispatch('actionEnsureSession');
      }
      // Sans question renvoyée, l'écran 3 est sauté et on file en 2b : c'est
      // le parcours prévu, pas une anomalie.
      commit('setStatus', 'questions');
      return res;
    },
    // C6 — passe LLM n°2 : on enregistre les réponses puis on demande la
    // finalisation. Les deux appels sont enchaînés, l'écran 2b couvre l'attente.
    async actionSubmitAnswers({state, commit, dispatch}, payload = {}) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      commit('setStatus', 'preparing');
      commit('setError', null);
      const sessionId = await dispatch('actionEnsureSession');
      if (!sessionId) {
        commit('setStatus', 'error');
        commit('setError', 'missing_session_id');
        return {error: true, message: 'missing_session_id'};
      }
      const base = {companyId, sessionId};
      // Toute question part, même intouchée : une question sans option devient
      // `dont_know`, ce qui est une information, là où l'omettre voudrait dire
      // « pas posée » — pas la même chose pour le modèle.
      const answers = state.questions.map((question) => {
        const answer = state.answers[question.id] || {};
        const freeText = (answer.freeText || '').trim();
        if (!answer.optionId) {
          return {
            question_id: question.id,
            dont_know: true,
            ...(freeText ? {free_text: freeText} : {}),
          };
        }
        return {
          question_id: question.id,
          option_id: answer.optionId,
          ...(freeText ? {free_text: freeText} : {}),
        };
      });
      const answersRes = useFixtures()
        ? await fixtures.answers()
        : await callAPI(
            'POST',
            'assistedCategorization/answers',
            {...base, answers, generalNote: state.generalNote, ...payload},
            false,
            LLM_TIMEOUT,
          );
      if (answersRes.error) {
        commit('setStatus', 'error');
        commit('setError', answersRes.error);
        return answersRes;
      }
      const completeRes = useFixtures()
        ? await fixtures.complete()
        : await callAPI(
            'POST',
            'assistedCategorization/complete',
            base,
            false,
            LLM_TIMEOUT,
          );
      if (completeRes.error) {
        commit('setStatus', 'error');
        commit('setError', completeRes.error);
      }
      return completeRes;
    },
    // C8 — récupération du plan. `actionSubmitAnswers` renvoie déjà le résultat
    // de `complete` ; cette action sert à (re)lire le plan, y compris après un
    // rechargement de page en cours de parcours.
    async actionFetchPlan({state, commit, dispatch}, result) {
      commit('setStatus', 'preparing');
      // Les règles de l'écran 4 viennent de `getSuggestionsList` : la passe LLM
      // écrit les suggestions côté serveur, le front les relit par l'endpoint
      // existant. Il porte l'ID de catégorie, les mots-clés et `affected`.
      // En mode jeu d'essai on ne mélange pas : les règles viennent alors du
      // fixture, sinon les propositions de catégories (qui les référencent)
      // ne correspondraient plus.
      const suggestionsRes = useFixtures()
        ? {result: []}
        : await dispatch('bank/actionGetSuggestions', null, {root: true});
      const listed = suggestionsRes?.error ? [] : suggestionsRes?.result || [];
      // Les verdicts du LLM (C1) ne portent ni catégorie ni critères ; la liste
      // des suggestions, si. On joint sur l'identifiant : seules celles que le
      // LLM garde deviennent des cartes, enrichies de son motif.
      const verdicts = state.llmSuggestions || [];
      const kept = verdicts.filter((v) => v.verdict === 'keep');
      const suggestions = kept.length
        ? kept
            .map((verdict) => {
              const full = listed.find(
                (s) => s.id === verdict.suggestion_id,
              );
              return {
                ...(full || {}),
                id: verdict.suggestion_id,
                keywords: full?.keywords ?? verdict.keywords ?? [],
                affected: verdict.affected ?? full?.affected ?? 0,
              };
            })
            .sort((a, b) => (b.affected ?? 0) - (a.affected ?? 0))
        : listed;
      let payload = result;
      if (!payload) {
        const companyId = localStorage.getItem(FYGR_COMPANY);
        // Même garde qu'à l'envoi des réponses : pas de session, pas d'appel.
        const sessionId = await dispatch('actionEnsureSession');
        const res = useFixtures()
          ? await fixtures.complete()
          : sessionId
            ? await callAPI(
                'POST',
                'assistedCategorization/complete',
                {companyId, sessionId},
                false,
                LLM_TIMEOUT,
              )
            : {error: true, message: 'missing_session_id'};
        // Le plan peut exister sans C8 : les règles viennent déjà d'être lues.
        // On n'échoue que si on n'a rien du tout à montrer.
        if (res.error) {
          if (!suggestions.length) {
            commit('setStatus', 'error');
            commit('setError', res.error);
            return res;
          }
        } else {
          payload = res.result;
        }
      }
      commit('setPlan', {
        // Écran 5 : les propositions d'arborescence n'ont pas d'équivalent
        // dans `getSuggestionsList`, elles ne peuvent venir que de C8.
        categoryProposals:
          payload?.category_proposals ?? payload?.categoryProposals,
        rules: suggestions.length ? suggestions : payload?.rules,
        dropped: payload?.dropped_clusters ?? payload?.dropped,
        coverage: payload?.coverage,
      });
      commit('setStatus', 'ready');
      return payload;
    },
    // C9 — compte dédoublonné pour un lot de suggestions. Le serveur seul sait
    // combien d'opérations DISTINCTES un ensemble de mots-clés attrape.
    async actionCountSuggestions({state, dispatch}, suggestions) {
      const payload = (suggestions || [])
        .map((suggestion) => ({
          ...suggestion,
          keywords: (
            state.draft.suggestions[suggestion.id]?.keywords ||
            suggestion.keywords ||
            []
          ).filter(Boolean),
        }))
        .filter((suggestion) => suggestion.keywords.length);
      if (!payload.length) return 0;
      const res = await dispatch(
        'bank/actionGetAffectedTransactions',
        {suggestions: payload, limit: 1, offset: 0},
        {root: true},
      );
      if (res?.error) return null;
      return res?.result?.recordsFiltered ?? null;
    },
    // Recalcule le compteur (œil) de chaque carte auprès du serveur : un appel
    // par groupe. Le CTA, lui, compte des suggestions et n'a besoin de rien.
    async actionRefreshCounts({state, getters, commit, dispatch}) {
      await Promise.all(
        getters.planGroups.map(async (group) => {
          const enabled = group.suggestions.filter(
            (suggestion) => state.draft.suggestions[suggestion.id]?.enabled,
          );
          const count = await dispatch('actionCountSuggestions', enabled);
          if (typeof count === 'number') {
            commit('setGroupCount', {categoryId: group.categoryId, count});
          }
        }),
      );
    },
    // C9 — recompte une ligne de mots-clés. Seul le serveur sait combien
    // d'opérations un terme fraîchement saisi attrape ; les mots-clés d'une
    // même ligne se combinent en ET.
    async actionCountRow({state, commit, dispatch}, {suggestionId}) {
      const entry = state.draft.suggestions[suggestionId];
      if (!entry) return null;
      const keywords = entry.keywords.filter(Boolean);
      if (!keywords.length) {
        commit('setDraftSuggestionAffected', {suggestionId, affected: 0});
        return 0;
      }
      const suggestion = (state.plan.rules || []).find(
        (s) => s.id === suggestionId,
      );
      const res = await dispatch(
        'bank/actionGetAffectedTransactions',
        {
          // Nouveau payload : une liste de suggestions. On renvoie la
          // suggestion telle qu'elle est venue du serveur, mots-clés remplacés
          // par ceux en cours d'édition.
          suggestions: [{...(suggestion || {id: suggestionId}), keywords}],
          limit: 1,
          offset: 0,
        },
        {root: true},
      );
      if (res?.error) return null;
      const affected = res?.result?.recordsFiltered ?? 0;
      commit('setDraftSuggestionAffected', {suggestionId, affected});
      return affected;
    },
    // C10 (lecture) — état courant d'une suggestion, pour alimenter le panneau
    // d'édition avant modification des mots-clés.
    async actionGetSuggestion(_, suggestionId) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      return callAPI(
        'GET',
        `company/${companyId}/getCategorizationSuggestion/${suggestionId}`,
      );
    },
    // C10 (écriture) — persiste la suggestion éditée.
    async actionUpdateSuggestion(_, {suggestionId, payload}) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      return callAPI(
        'POST',
        `company/${companyId}/updateCategorizationSuggestion/${suggestionId}`,
        payload,
      );
    },
    // C11 — application du plan (créations/déplacements de catégories + règles).
    // C11 — application. Le payload est dérivé de la copie de travail : pour
    // chaque proposition de catégorie et chaque règle, accepté ou refusé ; pour
    // chaque ligne de mots-clés, les termes tels quels, l'état de la ligne et
    // son application aux factures. Une règle bloquée est refusée, jamais
    // envoyée comme acceptée.
    // Persiste une suggestion éditée (C10). `convertToRules` ne transporte que
    // des identifiants : un mot-clé modifié doit donc exister côté serveur
    // AVANT la conversion, sinon la règle serait créée avec l'ancien terme.
    async actionPersistSuggestion({state, dispatch}, suggestionId) {
      const entry = state.draft.suggestions[suggestionId];
      if (!entry) return null;
      const read = await dispatch('actionGetSuggestion', suggestionId);
      if (read?.error) return read;
      return dispatch('actionUpdateSuggestion', {
        suggestionId,
        payload: {
          ...(read?.result ?? {}),
          keywords: entry.keywords.filter(Boolean),
          is_for_invoice: entry.invoices,
        },
      });
    },
    // Conversion finale des suggestions retenues en règles. On enregistre
    // d'abord les suggestions retouchées, puis on convertit — l'appel ne
    // transporte que des identifiants.
    async actionApplyPlan({getters, commit, dispatch}, payload = {}) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      const suggestionIds = getters.keptSuggestions.map((s) => s.id);
      if (!useFixtures()) {
        const edited = getters.editedSuggestions.filter((s) =>
          suggestionIds.includes(s.id),
        );
        await Promise.all(
          edited.map((s) => dispatch('actionPersistSuggestion', s.id)),
        );
      }
      const res = useFixtures()
        ? await fixtures.convertToRules()
        : await callAPI(
            'POST',
            'assistedCategorization/convertToRules',
            {companyId, suggestionIds, ...payload},
            false,
            LLM_TIMEOUT,
          );
      if (res.error) {
        commit('setError', res.error);
      }
      return res;
    },
    // C12 — abandon / « ne plus proposer ». Pas d'endpoint au registre des
    // appels : on persiste côté front, par company.
    actionDismiss({commit}) {
      const companyId = localStorage.getItem(FYGR_COMPANY);
      if (companyId) {
        localStorage.setItem(dismissedKeyFor(companyId), '1');
      }
      commit('reset');
    },
    actionReset({commit}) {
      commit('reset');
    },
  },
};

/** Le parcours a-t-il déjà été rejeté (ou terminé) pour la company courante ? */
export const isAssistedCategorizationDismissed = () => {
  const companyId = localStorage.getItem(FYGR_COMPANY);
  if (!companyId) return false;
  return localStorage.getItem(dismissedKeyFor(companyId)) === '1';
};
